import { createContext, useCallback, useContext, useMemo } from "react";

import type { AgentMessageMetadata } from "@/features/agents/useAgentChat";
import type { SuggestedAgentsOutput } from "@/features/onboarding/types";

import { offerStatesInMessages } from "./model";
import type { AgentOffer, OfferChoice, OfferState } from "./types";

export type OfferActions = {
  /** Send the click as the attorney's next message. */
  choose: (offer: AgentOffer, choice: OfferChoice) => void;
  stateOf: (offerId: string) => OfferState | null;
  /** A turn is running: a click would have to wait, so the buttons do. */
  busy: boolean;
  /** Send plain text as the attorney's next message (a Follow-ups "Fix"). */
  say?: (text: string) => void;
  /** Resolve a paused Meet Your Agents card (onboarding). */
  resolveAgents?: (toolCallId: string, output: SuggestedAgentsOutput) => void;
};

const OfferActionsContext = createContext<OfferActions | null>(null);

export const OfferActionsProvider = OfferActionsContext.Provider;

/** Null outside a live chat (a read-only transcript shows the offer without buttons). */
export const useOfferActionsContext = (): OfferActions | null =>
  useContext(OfferActionsContext);

type OfferChat = {
  messages: readonly {
    role: string;
    parts: readonly unknown[];
    metadata?: unknown;
  }[];
  status: string;
  send: (text: string, metadata?: AgentMessageMetadata) => unknown;
  resolveSuggestAgents?: (
    toolCallId: string,
    output: SuggestedAgentsOutput
  ) => unknown;
};

/** The provider value for a chat: clicks go out through its `send`. */
export const useOfferActions = (chat: OfferChat): OfferActions => {
  const { messages, status, send, resolveSuggestAgents } = chat;
  const states = useMemo(() => offerStatesInMessages(messages), [messages]);
  const choose = useCallback(
    (offer: AgentOffer, choice: OfferChoice) =>
      void send(choice.label, {
        accepted_offer: { id: offer.id, choice: choice.id }
      }),
    [send]
  );
  const stateOf = useCallback(
    (offerId: string) => states.get(offerId) ?? null,
    [states]
  );
  const say = useCallback((text: string) => void send(text), [send]);
  const resolveAgents = useMemo(
    () =>
      resolveSuggestAgents
        ? (toolCallId: string, output: SuggestedAgentsOutput) =>
            void resolveSuggestAgents(toolCallId, output)
        : undefined,
    [resolveSuggestAgents]
  );
  const busy = status === "streaming" || status === "submitted";
  return useMemo(
    () => ({ choose, stateOf, busy, say, resolveAgents }),
    [choose, stateOf, busy, say, resolveAgents]
  );
};
